import { useCallback, useEffect, useState } from "react";

import {
  Badge,
  Box,
  ChakraModal,
  ChakraModalOverlay,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
  IconButton,
  Row,
  HtMedia,
} from "@hightouch/htui";

export type GalleryImage = {
  imageSrc: string;
  fileName: string;
  /** Optional per-image height cap (e.g. videos render shorter). */
  maxHeight?: string;
};

type FullScreenImageOverlayProps = {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Single-image API (back-compat). Prefer `images` + `initialIndex` for a
   * navigable gallery.
   */
  imageSrc?: string;
  fileName?: string;
  maxHeight?: string;
  /**
   * Gallery API: when more than one image is provided, left/right arrow keys
   * and on-screen prev/next controls let the user page through them.
   */
  images?: GalleryImage[];
  initialIndex?: number;
};

/**
 * Fullscreen image preview. Supports a single image or a navigable gallery.
 * In gallery mode, ArrowLeft/ArrowRight (and the prev/next buttons) move
 * between images with wrap-around; Escape and overlay clicks close it.
 */
export const FullScreenImageOverlay = ({
  imageSrc,
  fileName,
  isOpen,
  onClose,
  maxHeight,
  images,
  initialIndex = 0,
}: FullScreenImageOverlayProps) => {
  const galleryImages: GalleryImage[] =
    images && images.length > 0
      ? images
      : imageSrc != null
        ? [{ imageSrc, fileName: fileName ?? "", maxHeight }]
        : [];

  const imageCount = galleryImages.length;
  const hasMultiple = imageCount > 1;

  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Sync the active image to the requested index whenever the overlay opens
  // (or the caller targets a different starting image). The Chakra modal stays
  // mounted while closed, so a key-based reset isn't an option here.
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((index) => (index - 1 + imageCount) % imageCount);
  }, [imageCount]);

  const goToNext = useCallback(() => {
    setCurrentIndex((index) => (index + 1) % imageCount);
  }, [imageCount]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (hasMultiple && e.key === "ArrowLeft") {
        e.preventDefault();
        goToPrevious();
      } else if (hasMultiple && e.key === "ArrowRight") {
        e.preventDefault();
        goToNext();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, hasMultiple, goToPrevious, goToNext]);

  // Guard against an out-of-range index if the image list shrinks underneath us.
  const safeIndex = Math.min(currentIndex, Math.max(imageCount - 1, 0));
  const activeImage = galleryImages[safeIndex];

  if (!activeImage) {
    return null;
  }

  return (
    <ChakraModal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="full"
      closeOnEsc
      closeOnOverlayClick
    >
      <ChakraModalOverlay onClick={onClose}>
        <Box position="absolute" top={4} right={4} zIndex={2}>
          <IconButton
            icon={CloseIcon}
            variant="secondary"
            onClick={onClose}
            shape="circle"
            aria-label="Close full screen image"
          />
        </Box>
        {hasMultiple && (
          <>
            <Box
              position="absolute"
              left={4}
              top="50%"
              transform="translateY(-50%)"
              zIndex={2}
            >
              <IconButton
                icon={ChevronLeftIcon}
                variant="secondary"
                shape="circle"
                size="lg"
                aria-label="Previous image"
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
              />
            </Box>
            <Box
              position="absolute"
              right={4}
              top="50%"
              transform="translateY(-50%)"
              zIndex={2}
            >
              <IconButton
                icon={ChevronRightIcon}
                variant="secondary"
                shape="circle"
                size="lg"
                aria-label="Next image"
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
              />
            </Box>
            <Row
              position="absolute"
              bottom={6}
              left="50%"
              transform="translateX(-50%)"
              zIndex={2}
              onClick={(e) => e.stopPropagation()}
            >
              <Badge variant="dark" size="sm" textVariant="numeric">
                {safeIndex + 1} / {imageCount}
              </Badge>
            </Row>
          </>
        )}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100vh"
          padding={8}
        >
          <HtMedia
            src={activeImage.imageSrc}
            alt={activeImage.fileName}
            maxWidth="90vw"
            maxHeight={activeImage.maxHeight ?? maxHeight ?? "90vh"}
            objectFit="contain"
          />
        </Box>
      </ChakraModalOverlay>
    </ChakraModal>
  );
};

/**
 * Small helper for groups of thumbnails that share one fullscreen gallery: the
 * caller derives the ordered `GalleryImage[]` during render, while this hook
 * tracks which image is open. Render the returned `<overlay>` once per group
 * and call `openGallery(index)` from each thumbnail's click handler.
 */
export const useImageGalleryOverlay = (images: GalleryImage[]) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const openGallery = useCallback((index: number) => {
    setOpenIndex(index);
  }, []);

  const close = useCallback(() => setOpenIndex(null), []);

  const overlay = (
    <FullScreenImageOverlay
      images={images}
      initialIndex={openIndex ?? 0}
      isOpen={openIndex !== null}
      onClose={close}
    />
  );

  return { openGallery, overlay };
};
